import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useSettings } from "@/context/SettingsContext";
import { Settings as SettingsIcon, Upload, Trash2, Image as ImageIcon, MessageCircle, Copy, Send, Check } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const TABS = [
  { key: "logo", label: "Logo", icon: ImageIcon },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
];

function LogoTab() {
  const { settings, update } = useSettings();
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const currentLogo = preview !== null ? preview : settings.logo_data_url;

  function onPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 2 * 1024 * 1024) return toast.error("Image is too large (max 2MB)");
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  }
  async function save() {
    setSaving(true);
    try {
      await update({ logo_data_url: preview });
      toast.success("Logo saved");
      setPreview(null);
    } catch (e) {
      toast.error("Failed to save logo");
    } finally {
      setSaving(false);
    }
  }
  async function removeLogo() {
    setSaving(true);
    try {
      await update({ logo_data_url: "" });
      setPreview(null);
      toast.success("Logo removed");
    } catch (e) {
      toast.error("Failed to remove logo");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 p-4 border border-[#E2E2E8] rounded-md bg-slate-50">
        <div className="w-20 h-20 rounded-md bg-white border border-[#E2E2E8] flex items-center justify-center overflow-hidden">
          {currentLogo ? (
            <img src={currentLogo} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
          ) : (
            <ImageIcon className="w-8 h-8 text-slate-400" />
          )}
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">{settings.app_name || "Maan group"}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {currentLogo ? "This is what appears on the login page and sidebar." : "No logo uploaded yet. Add one below."}
          </div>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="logo-file-input" />
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => inputRef.current?.click()}
          data-testid="logo-choose"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#E2E2E8] rounded-md hover:bg-slate-50 transition-colors duration-150"
        >
          <Upload className="w-4 h-4" /> {settings.logo_data_url ? "Replace logo" : "Choose image"}
        </button>
        {settings.logo_data_url && (
          <button
            onClick={removeLogo}
            disabled={saving}
            data-testid="logo-remove"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-red-200 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-150"
          >
            <Trash2 className="w-4 h-4" /> Remove
          </button>
        )}
      </div>
      <div className="flex justify-end pt-2 border-t border-[#E2E2E8]">
        <button
          onClick={save}
          disabled={saving || preview === null}
          data-testid="logo-save"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md transition-colors duration-150 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save logo"}
        </button>
      </div>
    </div>
  );
}

function WhatsAppTab() {
  const { settings, update, reload } = useSettings();
  const [form, setForm] = useState({
    twilio_sid: settings.twilio_sid || "",
    twilio_token: "",
    twilio_from: settings.twilio_from || "whatsapp:+14155238886",
    whatsapp_to: settings.whatsapp_to || "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [copied, setCopied] = useState(false);
  const webhookUrl = `${API}/whatsapp/webhook`;
  function set(key, val) { setForm((f) => ({ ...f, [key]: val })); }
  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.twilio_token) delete payload.twilio_token;
      await update(payload);
      await reload();
      toast.success("WhatsApp settings saved");
    } catch (e) {
      toast.error("Failed to save: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }
  async function sendTest() {
    setTesting(true);
    try {
      await api.post("/whatsapp/send", { body: "Maan group test message — you're connected." });
      toast.success("Test sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setTesting(false); }
  }
  function copyWebhook() {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  const configured = settings.twilio_sid && settings.twilio_token_set && settings.twilio_from && settings.whatsapp_to;

  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 space-y-1">
        <div className="font-medium">Twilio WhatsApp setup</div>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Sign up at console.twilio.com and open Messaging → Try WhatsApp → Sandbox.</li>
          <li>From your phone, WhatsApp <span className="font-mono-data">join &lt;code&gt;</span> to the sandbox number.</li>
          <li>Copy your Account SID and Auth Token, paste them below.</li>
          <li>Set the sandbox <span className="font-medium">"When a message comes in"</span> webhook to the URL below.</li>
        </ol>
      </div>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Twilio Account SID</span>
        <input value={form.twilio_sid} onChange={(e) => set("twilio_sid", e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono-data text-sm" data-testid="twilio-sid" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Twilio Auth Token {settings.twilio_token_set && <span className="ml-2 text-emerald-600">✓ saved</span>}</span>
        <input value={form.twilio_token} onChange={(e) => set("twilio_token", e.target.value)} placeholder={settings.twilio_token_set ? "(leave blank to keep)" : "Auth token"} type="password" className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono-data text-sm" data-testid="twilio-token" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Twilio WhatsApp From</span>
        <input value={form.twilio_from} onChange={(e) => set("twilio_from", e.target.value)} placeholder="whatsapp:+14155238886" className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono-data text-sm" data-testid="twilio-from" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Your WhatsApp Number</span>
        <input value={form.whatsapp_to} onChange={(e) => set("whatsapp_to", e.target.value)} placeholder="whatsapp:+15551234567" className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono-data text-sm" data-testid="whatsapp-to" />
      </label>
      <div className="p-3 bg-slate-50 border border-[#E2E2E8] rounded-md">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Inbound Webhook URL</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs font-mono-data bg-white border border-[#E2E2E8] rounded px-2 py-1.5 truncate" data-testid="webhook-url">{webhookUrl}</code>
          <button onClick={copyWebhook} className="p-2 text-slate-600 hover:bg-slate-200 rounded" data-testid="copy-webhook">
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E2E2E8]">
        <button onClick={sendTest} disabled={testing || !configured} data-testid="whatsapp-test" className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#E2E2E8] rounded-md hover:bg-slate-50 disabled:opacity-50">
          <Send className="w-4 h-4" /> {testing ? "Sending..." : "Send test"}
        </button>
        <button onClick={save} disabled={saving} data-testid="whatsapp-save" className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md disabled:opacity-60">
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function BrandSettingsDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("logo");
  const { reload } = useSettings();

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) reload(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="open-brand-settings" className="flex items-center gap-2 px-3 py-2 w-full rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors duration-150">
            <SettingsIcon className="w-4 h-4" /> Settings
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>Upload your logo and connect WhatsApp for two-way access.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-[#E2E2E8] -mx-6 px-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`settings-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors duration-150 ${
                tab === t.key ? "border-[#2563EB] text-[#2563EB] font-medium" : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-2">
          {tab === "logo" ? <LogoTab /> : <WhatsAppTab />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
